/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/CommandPanel.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Sparkles, SquarePen, CircleHelp, X, ArrowUp, Square, Crosshair, ShieldCheck, FileText, RotateCcw, LoaderCircle, CircleCheck, CircleAlert, MessageSquare, CornerDownRight, Building2 } from 'lucide-react';
import { api, streamChat, type AgentEvent } from '../lib/api';
import { useApp } from '../lib/store';
import { navigate } from '../lib/router';
import { fromHistory, upsertStep, type Item, type Step } from '../lib/thread';
import { I, AutoTextarea, LoadingRows } from './ui';
import { AgentCard, Steps } from './AgentCards';
import { CommandCard, ChoiceCard, COMMAND_ICONS, type CommandData } from './CommandCard';
import { Tour } from '../vendor/tour.js';

import { useLang } from '../lib/i18n';

const MONTH_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Suggestions follow the page: Command knows what the user is looking at. */
function suggestions(page: string, today: string, hasSelection: boolean, lang: 'de' | 'en'): string[] {
  const months = lang === 'en' ? MONTH_EN : MONTH_DE;
  const month = months[Number(today.slice(5, 7)) - 1] ?? (lang === 'en' ? 'September' : 'September');
  const next = months[Number(today.slice(5, 7)) % 12] ?? (lang === 'en' ? 'October' : 'Oktober');
  const invoice = lang === 'en'
    ? `Invoice to Limmat MedTech AG for CHF 8’600 for Engineering ${next}`
    : `Rechnung an Limmat MedTech AG über CHF 8’600 für Engineering ${next}`;
  if (lang === 'en') {
    const map: Record<string, string[]> = {
      review: [hasSelection ? 'Approve this proposal' : 'Approve all proposals above 90%', 'Approve all proposals', 'Always book Swisscom to 6510 with VI81'],
      journal: [hasSelection ? 'Reverse this entry' : 'Reverse an entry', `6000 to 1020 CHF 4’850 Rent ${next}`, 'Book CHF 120 bank fees from 1020 to 6940'],
      bank: ['Run bank reconciliation', 'Open receivables', invoice],
      vat: ['What is the VAT for this quarter?', 'Prepare VAT return', 'What deadlines are coming up?'],
      payroll: [`Book payroll for ${month}`, `Payroll preview ${month}`, `AHV wage statement ${Number(today.slice(0, 4)) - 1}`],
      settings: ['Set four-eyes limit to CHF 2’000', 'Confidence threshold to 95%', 'Always book Swisscom to 6510 with VI81'],
    };
    return map[page] ?? [invoice, 'Approve all proposals above 90%', 'How is our liquidity?', `Book payroll for ${month}`];
  }
  const map: Record<string, string[]> = {
    review: [hasSelection ? 'Diesen Vorschlag freigeben' : 'Gib alle Vorschläge ab 90 % frei', 'Gib alle Vorschläge frei', 'Buche Swisscom immer auf 6510 mit VI81'],
    journal: [hasSelection ? 'Storniere diese Buchung' : 'Storniere eine Buchung', `6000 an 1020 CHF 4’850 Miete ${next}`, 'Buche CHF 120 Bankspesen von 1020 auf 6940'],
    bank: ['Bankabstimmung durchführen', 'Offene Debitoren', invoice],
    vat: ['Wie hoch ist die MWST dieses Quartal?', 'MWST abrechnen', 'Welche Fristen stehen an?'],
    payroll: [`Lohnlauf ${month} buchen`, `Lohnvorschau ${month}`, `AHV-Lohnbescheinigung ${Number(today.slice(0, 4)) - 1}`],
    settings: ['Setze die Vier-Augen-Grenze auf CHF 2’000', 'Konfidenz-Schwelle auf 95 %', 'Buche Swisscom immer auf 6510 mit VI81'],
  };
  return map[page] ?? [invoice, 'Gib alle Vorschläge ab 90 % frei', 'Wie steht die Liquidität?', `Lohnlauf ${month} buchen`];
}

function getQueries(lang: 'de' | 'en') {
  if (lang === 'en') {
    return [
      { title: 'Liquidity and overview', text: 'How is our liquidity?' },
      { title: 'Balance Sheet', text: 'Show me the balance sheet as of today' },
      { title: 'Income Statement', text: 'Income statement year to date' },
      { title: 'Current period VAT', text: 'What is the VAT for this quarter?' },
      { title: 'Open Receivables', text: 'Open receivables' },
      { title: 'Deadlines', text: 'What deadlines are coming up?' },
    ];
  }
  return [
    { title: 'Liquidität und Überblick', text: 'Wie steht die Liquidität?' },
    { title: 'Bilanz', text: 'Zeig mir die Bilanz per heute' },
    { title: 'Erfolgsrechnung', text: 'Erfolgsrechnung seit Jahresbeginn' },
    { title: 'MWST laufende Periode', text: 'Wie hoch ist die MWST dieses Quartal?' },
    { title: 'Offene Debitoren', text: 'Offene Debitoren' },
    { title: 'Fristen', text: 'Welche Fristen stehen an?' },
  ];
}

/** Minimal inline markdown: **bold**, `code`, keeps line breaks. */
function rich(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <b key={i}>{part.slice(2, -2)}</b>
      : part.startsWith('`') && part.endsWith('`') ? <code key={i} className="mono">{part.slice(1, -1)}</code>
        : <Fragment key={i}>{part}</Fragment>);
}

function StepLine({ steps, streaming, lang }: { steps: Step[]; streaming: boolean; lang: 'de' | 'en' }) {
  if (!steps.length) return null;
  const running = [...steps].reverse().find((s) => s.state === 'running');
  if (streaming && running) return <div className="cstep-live" role="status"><I icon={LoaderCircle} sm spin /><span>{running.label}</span></div>;
  const failed = steps.some((s) => s.state === 'error');
  const countLabel = steps.length === 1 ? steps[0].label : lang === 'en' ? `${steps.length} steps` : `${steps.length} Schritte`;
  return (
    <details className="csteps">
      <summary><I icon={failed ? CircleAlert : CircleCheck} sm /><span>{countLabel}</span></summary>
      <Steps steps={steps} />
    </details>
  );
}

const waitFor = (sel: string, ms = 1200) => new Promise<void>((res) => {
  const t0 = Date.now();
  const tick = () => (document.querySelector(sel) || Date.now() - t0 > ms ? res() : setTimeout(tick, 40));
  tick();
});

/**
 * Kontor Command: the operator on the side of every page (Cmd/Ctrl+J). Instructions in plain
 * German become drafts on confirmation cards; nothing changes until the human executes one.
 */
export function CommandPanel({ tid, tenantName, page, pageLabel }: { tid: string | null; tenantName?: string; page: string; pageLabel: string }) {
  const { lang, t } = useLang();
  const app = useApp();
  const open = app.commandOpen;
  const sel = app.commandSelection;
  const [items, setItems] = useState<Item[] | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState<{ id: string; op: 'exec' | 'cancel' } | null>(null);
  const [ctxOff, setCtxOff] = useState(false);
  const [slashActive, setSlashActive] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const hist = useRef<string[]>([]);
  const histAt = useRef(-1);
  const root = useRef<HTMLElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const slashList = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);
  const back = useRef<HTMLElement | null>(null);
  const handled = useRef(0);
  const tour = useRef<Tour | null>(null);

  const focusComposer = (range?: [number, number]) => requestAnimationFrame(() => requestAnimationFrame(() => {
    const el = input.current; if (!el || !root.current || root.current.hidden) return;
    el.focus({ preventScroll: true });
    const [a, b] = range ?? [el.value.length, el.value.length];
    el.setSelectionRange(a, b);
  }));
  // Follow the conversation, but a card taller than the panel opens at its top: the amount and
  // the counterparty are what the human checks first, the buttons come after reading.
  const toBottom = () => requestAnimationFrame(() => {
    const b = body.current; if (!b) return;
    const last = b.lastElementChild as HTMLElement | null;
    b.scrollTop = last && last.offsetHeight > b.clientHeight - 32 ? last.offsetTop - 16 : b.scrollHeight;
  });

  // History: the latest Command conversation of this Mandant (drafts show their current status).
  const prevTidRef = useRef<string | null>(null);
  useEffect(() => {
    abort.current?.abort();
    if (prevTidRef.current !== tid) {
      setItems(tid ? null : []);
      setThreadId(null);
      setDraft('');
      prevTidRef.current = tid;
    }
    if (!tid) return;
    let alive = true;
    (async () => {
      try {
        const threads = await api.get<any[]>(`/api/t/${tid}/threads?surface=command`);
        const th = threads[0];
        const msgs = th ? await api.get<any[]>(`/api/t/${tid}/threads/${th.id}/messages`) : [];
        if (!alive) return;
        setThreadId(th?.id ?? null); setItems(msgs.map((m) => fromHistory(m))); toBottom();
      } catch { if (alive) setItems([]); }
    })();
    return () => { alive = false; };
  }, [tid, app.version]);

  // Open → remember where focus came from and put the caret in the composer. Close → give focus back.
  useEffect(() => {
    if (!open) return;
    const a = document.activeElement as HTMLElement | null;
    if (a && !root.current?.contains(a) && a !== document.body) back.current = a;
    focusComposer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.commandFocus]);
  useEffect(() => {
    // History loaded while the panel was hidden could not be scrolled (no layout): catch up on open.
    if (open) { toBottom(); return; }
    const a = document.activeElement;
    if ((!a || a === document.body || root.current?.contains(a)) && back.current?.isConnected) back.current.focus({ preventScroll: true });
    tour.current?.destroy();
  }, [open]);

  const context = useMemo(() => (ctxOff ? null : { page, pageLabel, selection: sel }), [ctxOff, page, pageLabel, sel]);
  useEffect(() => setCtxOff(false), [page, sel?.id]);

  const activeTid = useRef<string | null>(tid);
  activeTid.current = tid;

  const send = useCallback(async (text: string) => {
    const tStr = text.trim();
    if (!tid || busy || !tStr) return;
    const streamTid = tid;
    const now = new Date().toISOString();
    const aid = `a-${Date.now()}`;
    setItems((xs) => [...(xs ?? []),
      { id: `u-${Date.now()}`, role: 'user', text: tStr, attachments: [], steps: [], cards: [], at: now },
      { id: aid, role: 'agent', text: '', attachments: [], steps: [], cards: [], streaming: true, at: now }]);
    setDraft(''); setSlashDismissed(false);
    hist.current = [...hist.current.filter((x) => x !== tStr), tStr].slice(-30); histAt.current = -1;
    setBusy(true); toBottom();
    const ctrl = new AbortController();
    abort.current = ctrl;
    const stillHere = () => activeTid.current === streamTid && !ctrl.signal.aborted;
    const upd = (f: (a: Item) => Item) => {
      if (activeTid.current !== streamTid) return;
      setItems((xs) => (xs ?? []).map((x) => (x.id === aid ? f(x) : x)));
    };
    try {
      await streamChat(streamTid, { threadId, text: tStr, attachments: [], surface: 'command', context }, (e: AgentEvent) => {
        if (!stillHere()) return;
        if (e.type === 'thread') setThreadId(e.threadId);
        else if (e.type === 'step') upd((a) => ({ ...a, steps: upsertStep(a.steps, e as Step) }));
        else if (e.type === 'text') upd((a) => ({ ...a, text: a.text + e.delta }));
        else if (e.type === 'card') { upd((a) => ({ ...a, cards: [...a.cards, e.card] })); toBottom(); }
        else if (e.type === 'error') upd((a) => ({ ...a, error: e.message, retry: e.retryable ? { text: tStr, atts: [] } : undefined }));
      }, ctrl.signal);
    } catch (e: any) {
      if (activeTid.current === streamTid) {
        if (e.name !== 'AbortError') upd((a) => ({ ...a, error: e.message ?? t('cmd.conn_interrupted'), retry: { text: tStr, atts: [] } }));
        else upd((a) => ({ ...a, text: a.text || t('cmd.aborted') }));
      }
    } finally {
      if (activeTid.current === streamTid) {
        upd((a) => ({ ...a, streaming: false }));
        toBottom(); focusComposer();
      }
      if (abort.current === ctrl) abort.current = null;
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid, busy, threadId, context, t]);

  /** Put a template in the composer; «…» marks the caret, «90» is pre-selected so typing replaces it. */
  const prefill = (tpl: string) => {
    const i = tpl.indexOf('«'); const j = tpl.indexOf('»', i);
    if (i < 0 || j < 0) { setDraft(tpl); focusComposer(); return; }
    const inner = tpl.slice(i + 1, j) === '…' ? '' : tpl.slice(i + 1, j);
    setDraft(tpl.slice(0, i) + inner + tpl.slice(j + 1));
    setSlashDismissed(true);
    focusComposer([i, i + inner.length]);
  };

  // Requests from the palette, the topbar or other pages: queued until the history has loaded.
  useEffect(() => {
    const r = app.commandRequest;
    if (!r || r.n === handled.current || items === null || !tid) return;
    handled.current = r.n;
    if (r.send && !busy) send(r.text); else prefill(r.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.commandRequest?.n, items === null, tid]);

  // ---- confirm / cancel / adjust
  const replaceCard = (card: CommandData) => setItems((xs) => (xs ?? []).map((m) => ({ ...m, cards: m.cards.map((c) => (c.kind === 'command' && c.commandId === card.commandId ? card : c)) })));
  const act = async (card: CommandData, op: 'exec' | 'cancel') => {
    if (acting || !tid) return;
    setActing({ id: card.commandId, op });
    try {
      const next = await api.post<CommandData>(`/api/t/${tid}/commands/${card.commandId}/${op === 'exec' ? 'execute' : 'cancel'}`);
      replaceCard(next);
      if (next.status === 'executed') { app.toast(next.result?.summary ?? t('cmd.executed')); app.bump(); }
      else if (next.status === 'stale') app.toast(t('cmd.stale'), 'error');
      else if (next.status === 'failed') app.toast(next.error ?? t('cmd.failed'), 'error');
    } catch (e: any) { app.toast(e.message, 'error'); }
    setActing(null); focusComposer();
  };
  const adjust = async (card: CommandData) => {
    if (card.status === 'draft') await act(card, 'cancel');
    setDraft(card.instruction ?? ''); setSlashDismissed(true); focusComposer();
  };

  /** The latest command card, if it is an open draft, owns Cmd+Enter. */
  const primary = useMemo(() => {
    for (const m of [...(items ?? [])].reverse()) for (const c of [...m.cards].reverse()) if (c.kind === 'command') return c.status === 'draft' ? (c as CommandData) : null;
    return null;
  }, [items]);

  // ---- slash menu (a searchable command list, combobox keys)
  const slashOpen = draft.startsWith('/') && !slashDismissed && !!tid;
  const slashItems = useMemo(() => {
    const list = [
      ...app.commandRegistry.map((c) => ({ id: c.kind, group: c.group, title: c.title, hint: c.examples[0] ?? '', text: c.template, send: false, icon: COMMAND_ICONS[c.icon] ?? Sparkles })),
      ...getQueries(lang).map((q) => ({ id: q.text, group: lang === 'en' ? 'Questions' : 'Fragen', title: q.title, hint: q.text, text: q.text, send: true, icon: MessageSquare })),
    ];
    const tokens = draft.slice(1).toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.length ? list.filter((x) => tokens.every((tStr) => `${x.title} ${x.hint} ${x.group}`.toLowerCase().includes(tStr))) : list;
  }, [draft, app.commandRegistry, lang]);
  useEffect(() => setSlashActive(0), [draft]);
  useEffect(() => { slashList.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [slashActive]);
  const pick = (x?: (typeof slashItems)[number]) => { if (!x) return; if (x.send) send(x.text); else prefill(x.text); };

  const close = () => app.closeCommand();
  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const n = slashItems.length;
    if (slashOpen) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashActive((a) => (n ? (a + 1) % n : 0)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSlashActive((a) => (n ? (a - 1 + n) % n : 0)); return; }
      if (e.key === 'PageDown') { e.preventDefault(); setSlashActive((a) => Math.min(n - 1, a + 6)); return; }
      if (e.key === 'PageUp') { e.preventDefault(); setSlashActive((a) => Math.max(0, a - 6)); return; }
      if (e.key === 'Escape') { e.preventDefault(); setSlashDismissed(true); return; }
      if (e.key === 'Tab') { setSlashDismissed(true); return; }
      if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.nativeEvent.isComposing) { e.preventDefault(); n ? pick(slashItems[slashActive]) : send(draft.slice(1)); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if ((e.metaKey || e.ctrlKey) && !draft.trim() && primary) { act(primary, 'exec'); return; }
      send(draft); return;
    }
    if (e.key === 'Escape') { e.preventDefault(); if (busy) abort.current?.abort(); else close(); return; }
    const el = e.currentTarget;
    if (e.key === 'ArrowUp' && hist.current.length && el.selectionStart === 0 && el.selectionEnd === 0 && (!draft || histAt.current >= 0)) {
      e.preventDefault();
      histAt.current = histAt.current < 0 ? hist.current.length - 1 : Math.max(0, histAt.current - 1);
      setDraft(hist.current[histAt.current]); setSlashDismissed(true);
    } else if (e.key === 'ArrowDown' && histAt.current >= 0 && el.selectionStart === el.value.length) {
      e.preventDefault();
      histAt.current = histAt.current + 1 < hist.current.length ? histAt.current + 1 : -1;
      setDraft(histAt.current < 0 ? '' : hist.current[histAt.current]);
    }
  };

  const newThread = () => { abort.current?.abort(); setItems([]); setThreadId(null); setDraft(''); focusComposer(); };

  // ---- first-run tour: the idea (approval-first operator next to every page), not the controls
  const startTour = (force: boolean) => {
    tour.current?.destroy();
    const tTour = new Tour({
      storageKey: `kontor-command-tour-${lang}-v1`,
      labels: {
        skip: lang === 'en' ? 'Skip tour' : 'Tour überspringen',
        back: lang === 'en' ? 'Back' : 'Zurück',
        next: lang === 'en' ? 'Next' : 'Weiter',
        done: lang === 'en' ? 'Got it' : 'Verstanden',
        step: (i: number, n: number) => (lang === 'en' ? `STEP ${i}/${n}` : `SCHRITT ${i}/${n}`),
      },
      onFinish: () => setDraft((d) => (d === '/' ? '' : d)),
      onSkip: () => setDraft((d) => (d === '/' ? '' : d)),
      steps: [
        {
          target: '[data-tour="cmd-composer"]',
          title: lang === 'en' ? 'Just say what needs to be done' : 'Sag einfach, was zu tun ist',
          body: lang === 'en'
            ? 'Create invoices, approve proposals, reverse entries, book payroll, or ask questions: in plain English and from any page. ⌘J opens and closes Command.'
            : 'Rechnungen stellen, Vorschläge freigeben, stornieren, Löhne buchen oder eine Frage stellen: in normalem Deutsch und von jeder Seite aus. ⌘J öffnet und schliesst Command.',
          placement: 'top',
          before: () => setDraft((d) => (d === '/' ? '' : d)),
        },
        {
          target: '[data-tour="cmd-context"]',
          title: lang === 'en' ? 'Command sees what you see' : 'Command sieht, was du siehst',
          body: lang === 'en'
            ? 'Page and selection travel along. "Approve this proposal" refers to the open proposal, "Reverse this entry" to the open entry. Click × to detach the context.'
            : 'Seite und Auswahl gehen mit. «Diesen Vorschlag freigeben» bezieht sich auf den geöffneten Vorschlag, «Storniere diese Buchung» auf die geöffnete Buchung. Mit × lässt du den Bezug weg.',
          placement: 'top',
          before: () => setDraft((d) => (d === '/' ? '' : d)),
        },
        {
          target: '[data-tour="cmd-slash"]',
          title: lang === 'en' ? 'Type / for all commands' : 'Tippe / für alle Befehle',
          body: lang === 'en'
            ? 'The list shows everything Command can handle, with examples. Continue typing to filter, arrow keys to select, Enter to apply the template.'
            : 'Die Liste zeigt alles, was Command erledigen kann, mit Beispielen. Weitertippen filtert, Pfeiltasten wählen, Enter übernimmt die Vorlage.',
          placement: 'top',
          before: async () => { setSlashDismissed(false); setDraft('/'); await waitFor('[data-tour="cmd-slash"]'); },
        },
        {
          target: '[data-tour="cmd-trust"]',
          title: lang === 'en' ? 'Draft first, then execute' : 'Erst Entwurf, dann Ausführen',
          body: lang === 'en'
            ? 'Every change appears as a draft with amounts and posting lines. Only your click or ⌘↵ executes it. If data changed in the meantime, Kontor refuses execution rather than doing something you never saw.'
            : 'Jede Änderung erscheint als Entwurf mit Beträgen und Buchungszeilen. Erst dein Klick oder ⌘↵ führt sie aus. Haben sich die Daten inzwischen geändert, verweigert Kontor die Ausführung, statt etwas zu tun, das du nie gesehen hast.',
          placement: 'top',
          before: () => setDraft((d) => (d === '/' ? '' : d)),
        },
      ],
    });
    tour.current = tTour;
    if (force) tTour.start(); else tTour.startIfUnseen();
  };
  useEffect(() => {
    if (!open || !tid || items === null) return;
    const h = setTimeout(() => { if (!document.querySelector('.tour-root:not([hidden])')) startTour(false); }, 450);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tid, items === null]);
  useEffect(() => () => tour.current?.destroy(), []);

  const today = app.health?.today ?? new Date().toISOString().slice(0, 10);
  let lastGroup = '';

  return (
    <aside ref={root} className="command" aria-label="Command" hidden={!open}
      // Page shortcuts (Review's j/k/a/r, drawers' Esc) must not fire while working in the panel.
      onKeyDown={(e) => e.stopPropagation()}>
      <header className="command-head">
        <span className="command-mark" aria-hidden><I icon={Sparkles} sm /></span>
        <div className="t"><b>Command</b><span>{tenantName ?? t('cmd.no_tenant_selected')}</span></div>
        <button className="btn ghost icon-only" onClick={newThread} disabled={busy || !items?.length} title={t('cmd.new_instruction')} aria-label={t('cmd.new_instruction')}><I icon={SquarePen} /></button>
        <button className="btn ghost icon-only" onClick={() => startTour(true)} disabled={!tid} title={t('cmd.tour')} aria-label={t('cmd.tour')}><I icon={CircleHelp} /></button>
        <button className="btn ghost icon-only" onClick={close} title={t('cmd.close')} aria-label={t('cmd.close')}><I icon={X} /></button>
      </header>

      <div className="command-body" ref={body} aria-live="polite" aria-busy={busy}>
        {!tid && (
          <div className="command-empty">
            <h3>{t('cmd.no_tenant_title')}</h3>
            <p className="muted">{t('cmd.no_tenant_body')}</p>
            <div className="cmd-suggest">{app.tenants.map((ten) => <button key={ten.id} onClick={() => navigate(`/t/${ten.id}/cockpit`)}><I icon={Building2} sm /><span>{ten.name}</span></button>)}</div>
          </div>
        )}
        {tid && items === null && <LoadingRows rows={4} />}
        {tid && items?.length === 0 && (
          <div className="command-empty">
            <h3>{t('cmd.empty_title')}</h3>
            <p className="muted">{t('cmd.empty_body')}</p>
            <div className="cmd-suggest" data-tour="cmd-suggestions">
              <span className="label">{t('cmd.suggestions_for', { page: pageLabel })}</span>
              {suggestions(page, today, !!sel, lang).map((s) => <button key={s} onClick={() => send(s)}><I icon={CornerDownRight} sm /><span>{s}</span></button>)}
            </div>
          </div>
        )}
        {items?.map((m) => m.role === 'user' ? (
          <div key={m.id} className="cmsg user"><div className="bubble">{m.text}</div></div>
        ) : (
          <div key={m.id} className="cmsg agent">
            <StepLine steps={m.steps} streaming={!!m.streaming} lang={lang} />
            {m.text && <div className="msg-text">{rich(m.text)}</div>}
            {m.streaming && !m.text && !m.steps.length && <span className="typing" aria-label={t('cmd.working')}><i /><i /><i /></span>}
            {m.cards.map((c, i) => c.kind === 'command'
              ? <CommandCard key={c.commandId} tid={tid!} card={c} primary={c.commandId === primary?.commandId} acting={acting && acting.id === c.commandId ? acting.op : null}
                  onExecute={() => act(c, 'exec')} onCancel={() => act(c, 'cancel')} onAdjust={() => adjust(c)} onSend={send} />
              : c.kind === 'choice' ? <ChoiceCard key={i} card={c} onSend={send} disabled={busy} />
                : c.kind === 'document' ? <div key={i} className="chip"><I icon={FileText} sm /><span>{c.filename} · {c.summary}</span></div>
                  : <AgentCard key={i} tid={tid!} card={c} />)}
            {m.error && (
              <div className="error-card" role="alert">
                <span className="grow">{m.error}</span>
                {m.retry && <button className="btn sm" onClick={() => { const r = m.retry!; setItems((xs) => (xs ?? []).filter((x) => x.id !== m.id)); send(r.text); }}><I icon={RotateCcw} sm /> {t('cmd.resend')}</button>}
              </div>
            )}
          </div>
        ))}
      </div>

      <footer className="command-foot">
        {slashOpen && (
          <div className="slash-menu" role="listbox" id="cmd-slash-list" ref={slashList} data-tour="cmd-slash" aria-label={t('cmd.slash_aria')}>
            {!slashItems.length && <div className="combo-empty">{t('cmd.slash_empty')}</div>}
            {slashItems.map((x, i) => {
              const head = x.group !== lastGroup ? <div className="group-label" key={`g-${x.group}`}>{x.group}</div> : null;
              lastGroup = x.group;
              return [head, (
                <div key={x.id} id={`cmd-opt-${i}`} role="option" aria-selected={i === slashActive} className="combo-opt" onMouseMove={() => setSlashActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(x); }}>
                  <I icon={x.icon} sm /><span className="primary">{x.title}</span><span className="secondary">{x.hint}</span>
                </div>
              )];
            })}
          </div>
        )}
        <div className="composer" data-tour="cmd-composer">
          {tid && !ctxOff && (
            <div className="ctx-row" data-tour="cmd-context">
              <span className="ctx-chip" title={t('cmd.context_tip')}>
                <I icon={Crosshair} sm /><span>{pageLabel}{sel ? ` · ${sel.label}` : ''}</span>
                <button onClick={() => { setCtxOff(true); focusComposer(); }} aria-label={t('cmd.remove_context')}><I icon={X} sm /></button>
              </span>
            </div>
          )}
          <AutoTextarea inputRef={input} value={draft} onChange={(v) => { setDraft(v); if (!v.startsWith('/')) setSlashDismissed(false); histAt.current = -1; }} onKeyDown={onKey} minRows={1} maxRows={8} className="" disabled={!tid}
            placeholder={tid ? t('cmd.placeholder_active') : t('cmd.placeholder_inactive')} aria-label={t('cmd.aria_input')}
            role="combobox" aria-expanded={slashOpen} aria-controls="cmd-slash-list" aria-activedescendant={slashOpen && slashItems.length ? `cmd-opt-${slashActive}` : undefined} />
          <div className="composer-bar">
            <button className="btn ghost sm" onClick={() => { setSlashDismissed(false); setDraft('/'); focusComposer(); }} disabled={!tid} title={t('cmd.btn_commands_tip')}><kbd>/</kbd> {t('cmd.btn_commands')}</button>
            <span className="grow dim hint">{primary ? <><kbd>⌘</kbd> <kbd>↵</kbd> {t('cmd.hint_exec')}</> : <><kbd>↵</kbd> {t('cmd.hint_send')} · <kbd>↑</kbd> {t('cmd.hint_prev')}</>}</span>
            {busy
              ? <button className="btn sm" onClick={() => abort.current?.abort()} aria-label={t('cmd.stop')}><I icon={Square} sm /> {t('cmd.stop')}</button>
              : <button className="btn primary icon-only" aria-label={t('cmd.send')} onClick={() => send(draft)} disabled={!draft.trim() || !tid}><I icon={ArrowUp} /></button>}
          </div>
        </div>
        <p className="command-trust" data-tour="cmd-trust"><I icon={ShieldCheck} sm /><span>{t('cmd.trust')}</span></p>
      </footer>
    </aside>
  );
}
